import { useApolloClient } from '@apollo/client';
import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import {
  ToastAlertVariant,
  ToastVariant
} from '@aurora/shared-client/components/common/ToastAlert/enums';
import type ToastProps from '@aurora/shared-client/components/common/ToastAlert/ToastAlertProps';
import useFrameEnd from '@aurora/shared-client/components/context/AnalyticsParentFrames/useFrameEnd';
import ParentFrameSupplier from '@aurora/shared-utils/analytics/ParentFrameSupplier';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useToasts from '@aurora/shared-client/components/context/ToastContext/useToasts';
import useMutationWithTracing from '@aurora/shared-client/components/useMutationWithTracing';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import useRegistrationStatus from '@aurora/shared-client/components/users/useRegistrationStatus';
import handleErrorStates from '@aurora/shared-client/helpers/form/MutationError/MutationErrorHelper';
import Icons from '@aurora/shared-client/icons';
import { NodeType } from '@aurora/shared-types/nodes/enums';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import { checkPolicy } from '@aurora/shared-utils/helpers/objects/PolicyResultHelper';
import { getLog } from '@aurora/shared-utils/log';
import dynamic from 'next/dynamic';
import React, { useContext, useState } from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import type { ErrorOption } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form/dist/types/path';
import { useUIDSeed } from 'react-uid';
import { updateFollowsCacheAfterSubscription } from '../../../helpers/subscriptions/SubscriptionHelper';
import { SubscriptionActionVariant } from '../../../types/enums';
import type {
  DeleteTagFollowMutation,
  DeleteTagFollowMutationVariables,
  FollowViewNodeFragment,
  MuteTagFollowForDescendantNodeMutation,
  MuteTagFollowForDescendantNodeMutationVariables,
  TagFollowMutation,
  TagFollowMutationVariables,
  TagPoliciesQuery,
  TagPoliciesQueryVariables,
  TagSubscriptionStatusQuery,
  TagSubscriptionStatusQueryVariables
} from '../../../types/graphql-types';
import useTranslation from '../../useTranslation';
import deleteTagFollowMutation from './DeleteTagFollow.mutation.graphql';
import muteTagFollowForDescendantNodeMutation from './MuteTagFollowForDescendantNode.mutation.graphql';
import tagFollowMutation from './TagFollowModal/TagFollow.mutation.graphql';
import canFollowTagQuery from './TagPolicies.query.graphql';
import tagSubscriptionStatusQuery from './TagSubscriptionStatus.query.graphql';

const TagFollowModal = dynamic(() => import('./TagFollowModal/TagFollowModal'), {
  ssr: false
});

const log = getLog(module);

interface Props {
  /**
   * The tag name
   */
  tagName: string;
  /**
   * The view variant
   */
  variant?: SubscriptionActionVariant;
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
  /**
   * Function executed after a subscription delete is completed.
   */
  onSubscriptionDeleteCompleted?: () => void;
  /**
   * The subscribed node information
   */
  node?: FollowViewNodeFragment;
}
/**
 * A tag subscription component with variants to subscribe/unsubscribe to tags.
 *
 * @constructor
 * @author Deepak Yadav
 */
const TagSubscriptionAction: React.FC<React.PropsWithChildren<Props>> = ({
  tagName,
  variant = SubscriptionActionVariant.ICON,
  className,
  onSubscriptionDeleteCompleted,
  node
}) => {
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.TAG_SUBSCRIPTION_ACTION
  );
  const [showTagFollowModal, setShowTagFollowModal] = useState<boolean>(false);
  const { contextNode } = useContext(AppContext);
  const { isAnonymous } = useRegistrationStatus();
  const uidSeed = useUIDSeed();
  const { addToast } = useToasts();
  const cx = useClassNameMapper();
  const nodeIdForTopLevel = 'category:top';
  const subscribedCoreNode = node ?? contextNode;
  const nodeId =
    subscribedCoreNode.nodeType === NodeType.COMMUNITY ? nodeIdForTopLevel : subscribedCoreNode.id;
  const { cache } = useApolloClient();
  const [tagFollowMutate, { loading: createLoading }] = useMutationWithTracing<
    TagFollowMutation,
    TagFollowMutationVariables
  >(module, tagFollowMutation);

  const [muteTagFollowForDescendantNodeMutate] = useMutationWithTracing<
    MuteTagFollowForDescendantNodeMutation,
    MuteTagFollowForDescendantNodeMutationVariables
  >(module, muteTagFollowForDescendantNodeMutation);
  const [frameEnd] = useFrameEnd();
  const { data: tagSubscriptionData, loading: tagSubscriptionStatusQueryLoading } =
    useQueryWithTracing<TagSubscriptionStatusQuery, TagSubscriptionStatusQueryVariables>(
      module,
      tagSubscriptionStatusQuery,
      {
        variables: {
          nodeIds: nodeId,
          tagText: tagName
        },
        fetchPolicy: 'network-only'
      }
    );

  const { data: policiesData, loading: policiesLoading } = useQueryWithTracing<
    TagPoliciesQuery,
    TagPoliciesQueryVariables
  >(module, canFollowTagQuery, {
    variables: {
      nodeId: contextNode.id
    }
  });

  const isSubscribed = !!tagSubscriptionData?.tagFollowsForNodes[0]?.follow?.id;
  const [deleteSubscriptionMutate, { loading: deleteLoading }] = useMutationWithTracing<
    DeleteTagFollowMutation,
    DeleteTagFollowMutationVariables
  >(module, deleteTagFollowMutation);

  if (textLoading || isAnonymous || tagSubscriptionStatusQueryLoading || policiesLoading) {
    return null;
  }

  const canFollowTag = checkPolicy(policiesData?.coreNode?.tagPolicies?.canSubscribeTagOnNode);

  const isCommunity = node
    ? node.nodeType === NodeType.CATEGORY && node.displayId === 'top'
    : contextNode.nodeType === NodeType.COMMUNITY;

  /**
   * Renders a toast describing the error result of a subscriptions action
   * @param title Title to be displayed
   * @param body Toast body
   * @param autohide whether the toast should be hidden automatically after certain time interval
   */
  function renderSuccessFeedback(title: string, body: string, autohide = true): void {
    const toastProps: ToastProps = {
      toastVariant: ToastVariant.FLYOUT,
      alertVariant: ToastAlertVariant.INFO,
      id: uidSeed(title),
      title,
      icon: isSubscribed ? Icons.UnfollowIcon : Icons.FollowingIcon,
      autohide,
      message: body,
      delay: 4000
    };
    addToast(toastProps);
  }

  /**
   * Renders a toast describing the error result of a subscriptions action
   * @param title Title to be displayed
   * @param body Toast body
   * @param autohide whether the toast should be hidden automatically after certain time interval
   */
  function renderErrorFeedback(title: string, body: string, autohide = true): void {
    const toastProps: ToastProps = {
      toastVariant: ToastVariant.BANNER,
      alertVariant: ToastAlertVariant.DANGER,
      id: uidSeed(title),
      title,
      autohide,
      message: body,
      delay: 4000
    };
    addToast(toastProps);
  }

  /**
   * Function that handles the unfollow tag action in board/category/grouphub or across community.
   */
  async function handleUnfollowTag() {
    let setError: (name: FieldPath<FormData>, error: ErrorOption) => void;
    let error;
    const cachedTagSubscriptionData = cache.readQuery<
      TagSubscriptionStatusQuery,
      TagSubscriptionStatusQueryVariables
    >({
      query: tagSubscriptionStatusQuery,
      variables: {
        nodeIds: nodeId,
        tagText: tagName
      }
    });

    const { coreNode: subscribedNode } = cachedTagSubscriptionData?.tagFollowsForNodes[0]?.follow;
    const { coreNode: currentNode } = cachedTagSubscriptionData?.tagFollowsForNodes[0];

    /**
     * When current node is explicitly subscribed, use DeleteTagFollowMutation. Else use, MuteTagFollowForDescendantNodeMutation.
     */
    if (currentNode?.id !== subscribedNode?.id) {
      const mutationOptions: MuteTagFollowForDescendantNodeMutationVariables = {
        nodeId: contextNode.id,
        followId: `tag:${cachedTagSubscriptionData?.tagFollowsForNodes[0]?.follow?.id}`
      };

      const { errors, data: response } = await muteTagFollowForDescendantNodeMutate({
        variables: mutationOptions,
        refetchQueries: [
          {
            query: tagSubscriptionStatusQuery,
            variables: {
              nodeIds: nodeId,
              tagText: tagName
            }
          }
        ]
      });
      error = errors;

      if (response?.muteTagFollowForDescendantNode?.errors) {
        handleErrorStates(response?.muteTagFollowForDescendantNode?.errors, setError);
        return null;
      }
    } else {
      const deleteInput = {
        targetId: `subscription:${tagSubscriptionData?.tagFollowsForNodes[0]?.follow?.id}`
      };

      const mutationOptions: DeleteTagFollowMutationVariables = {
        deleteInput: deleteInput
      };
      const { errors, data: response } = await deleteSubscriptionMutate({
        variables: mutationOptions,
        update: (deleteTagFollowCache, { errors: queryErrors, data: queryData }): void => {
          if (!queryErrors?.length && !queryData?.deleteTagFollow?.errors) {
            updateFollowsCacheAfterSubscription(deleteTagFollowCache);
            if (onSubscriptionDeleteCompleted) {
              onSubscriptionDeleteCompleted();
            }
          }
        },
        refetchQueries: [
          {
            query: tagSubscriptionStatusQuery,
            variables: {
              nodeIds: nodeId,
              tagText: tagName
            }
          }
        ]
      });

      if (response?.deleteTagFollow?.errors) {
        handleErrorStates(response?.deleteTagFollow?.errors, setError);
        return null;
      }
      error = errors;
    }

    if (error?.length > 0) {
      log.error(error, 'Unable to follow this tag in %s', contextNode.nodeType);
      renderErrorFeedback(
        formatMessage('unexpected.error.title'),
        formatMessage('unexpected.error.message')
      );
      return null;
    }

    if (onSubscriptionDeleteCompleted) {
      onSubscriptionDeleteCompleted();
    }
    renderSuccessFeedback(
      formatMessage('success.unfollow.title'),
      formatMessage(
        isCommunity ? 'success.unfollowtagAcrossCommunity.message' : 'success.unfollowtag.message'
      )
    );
  }

  /**
   * Function that handles the tag follow action across community.
   */
  async function handleFollowTag() {
    const mutationOptions: TagFollowMutationVariables = {
      nodeIds: [contextNode.id],
      tagText: tagName
    };
    const { errors } = await tagFollowMutate({
      variables: mutationOptions,
      refetchQueries: [
        {
          query: tagSubscriptionStatusQuery,
          variables: {
            nodeIds: nodeId,
            tagText: tagName
          }
        }
      ],
      update: (followTagCache, { errors: followTagErrors }): void => {
        if (!followTagErrors?.length) {
          updateFollowsCacheAfterSubscription(followTagCache);
        }
      },
      onCompleted: (): void => {
        const parentFrameId = ParentFrameSupplier.getParentFrameId();
        frameEnd({
          context: { parentFrameId }
        });
      }
    });

    if (errors?.length > 0) {
      log.error('Unable to follow this tag across community', errors);
      renderErrorFeedback(
        formatMessage('unexpected.error.title'),
        formatMessage('unexpected.error.message')
      );
      return null;
    }

    renderSuccessFeedback(
      formatMessage('success.follow.title'),
      formatMessage('success.follow.message.followAcrossCommunity')
    );
  }

  /**
   * Function that handles which subscription action needs to performed on Tag.
   */
  async function handleSubscription() {
    if (contextNode.nodeType === NodeType.COMMUNITY && !isSubscribed) {
      await handleFollowTag();
    } else if (!isSubscribed) {
      setShowTagFollowModal(true);
    } else {
      setShowTagFollowModal(false);
      await handleUnfollowTag();
    }
  }

  /**
   * Renders the subscription icon variant.
   */
  function renderSubscriptionIconVariant(): React.ReactElement {
    const icon: Icons = isSubscribed ? Icons.FollowingIcon : Icons.FollowIcon;
    const title = formatMessage('buttonTitle', { isSubscribed });

    return (
      <Button
        variant={ButtonVariant.NO_VARIANT}
        className={cx('lia-g-icon-btn lia-g-ml-10')}
        title={title}
        aria-label={title}
        onClick={handleSubscription}
        disabled={createLoading || deleteLoading}
        data-testid="SubscriptionIconActionVariant"
      >
        <Icon icon={icon} size={IconSize.PX_20} color={IconColor.GRAY_900} />
      </Button>
    );
  }

  /**
   * Renders the tag follow modal
   */
  function renderTagFollowModal(): React.ReactElement {
    return (
      <TagFollowModal
        show={showTagFollowModal}
        onHide={() => {
          setShowTagFollowModal(false);
        }}
      />
    );
  }

  /**
   * Renders the dropdown item variant to unfollow tag.
   */
  function renderDropdownItemVariant(): React.ReactElement {
    return (
      <Dropdown.Item
        onClick={handleUnfollowTag}
        className={cx(className, 'text-nowrap')}
        data-testid="NodeSubscriptionDropdownActionVariant"
      >
        {formatMessage('unfollow')}
      </Dropdown.Item>
    );
  }

  const variantToComponentMap: Record<
    SubscriptionActionVariant,
    React.FC<React.PropsWithChildren<unknown>>
  > = {
    [SubscriptionActionVariant.ICON]: renderSubscriptionIconVariant,
    [SubscriptionActionVariant.DROPDOWN_ITEM]: renderDropdownItemVariant,
    [SubscriptionActionVariant.BUTTON]: null
  };

  const Component = variantToComponentMap[variant];

  return (
    canFollowTag && (
      <>
        <Component />
        {showTagFollowModal && renderTagFollowModal()}
      </>
    )
  );
};

export default TagSubscriptionAction;
